/**
 * OscillatorModule — a signal source.
 *
 * Graph: OscillatorNode → level gain → (output to next module in chain).
 *
 * Note shaping (attack/release) is no longer this module's job — the
 * Envelope module drives the VCA's gain via a modulation connection.
 */

import type { AudioEngine } from '../AudioEngine';
import { EnumParameter, NumericParameter, type AnyParameter } from '../../params/Parameter';
import type { ModuleState, SynthModule } from './SynthModule';
import { mintId, readParamState, writeParamState } from './util';

export type Waveform =
  | 'sine'
  | 'square'
  | 'saw'
  | 'triangle'
  | 'pulse'
  | 'capture';

const WAVEFORMS = [
  'sine',
  'square',
  'saw',
  'triangle',
  'pulse',
  'capture',
] as const;

/**
 * Logical waveform → OscillatorNode.type. `pulse` uses the sawtooth
 * source under the hood — Voice.ts then adds a DC offset and routes
 * the saw through a sign-function WaveShaper to synthesize a
 * variable-width pulse. `square` stays the cheap fixed-50% native
 * path (no extra nodes). `capture` doesn't drive an OscillatorNode
 * at all — the voice swaps to an AudioBufferSourceNode reading
 * the captured wavetable. The fallback type below is just a safe
 * value for the underlying (silent) OscillatorNode in that mode.
 */
function waveformToOscType(w: Waveform): OscillatorType {
  switch (w) {
    case 'sine':
      return 'sine';
    case 'square':
      return 'square';
    case 'saw':
      return 'sawtooth';
    case 'triangle':
      return 'triangle';
    case 'pulse':
      return 'sawtooth';
    case 'capture':
      return 'sine';
  }
}

export class OscillatorModule implements SynthModule {
  readonly kind = 'signal' as const;
  readonly type = 'oscillator' as const;
  readonly displayName = 'OSC';
  readonly id: string;
  bypass = false;

  readonly waveform = new EnumParameter<Waveform>({
    name: 'Wave',
    default: 'saw',
    options: WAVEFORMS,
    formatter: (v) =>
      ({
        sine: 'SIN',
        square: 'SQR',
        saw: 'SAW',
        triangle: 'TRI',
        pulse: 'PLS',
        capture: 'CAP',
      })[v],
  });
  /**
   * Coarse pitch offset in semitones. Stacks with `detune` (cents) and the
   * voice's note number to produce the final oscillator frequency.
   */
  readonly coarse = new NumericParameter({
    name: 'Coarse',
    default: 0,
    min: -24,
    max: 24,
    curve: 'linear',
    unit: 'st',
    smoothingMs: 0, // integer semitones, no point smoothing
    formatter: (v) => `${v > 0 ? '+' : ''}${v.toFixed(0)}`,
  });
  readonly detune = new NumericParameter({
    name: 'Detune',
    default: 0,
    min: -100,
    max: 100,
    curve: 'linear',
    unit: 'ct',
    smoothingMs: 10,
    formatter: (v) => `${v > 0 ? '+' : ''}${v.toFixed(0)}`,
  });
  readonly level = new NumericParameter({
    name: 'Level',
    default: 0.8,
    min: 0,
    max: 1,
    curve: 'linear',
    smoothingMs: 15,
    // Dice rolls bias toward loud (rng^(1/3)). Keeps Randomize audible.
    randomSkew: 3,
    formatter: (v) => `${Math.round(v * 100)}%`,
  });
  /**
   * Pulse width when Wave='pulse'. 0.5 = perfect square; values toward
   * 0 or 1 narrow the pulse on either side. Modulating this with an
   * LFO is the classic PWM growl — wire `LFO → OSC · Width` in the
   * mod matrix at audio rate.
   *
   * Width is a NumericParameter [0, 1] for an intuitive UI; Voice.ts
   * maps it internally to a [-1, +1] DC offset feeding the saw → sign
   * waveshaper graph. Audio-rate mod sources written to the mapped
   * AudioParam (see Voice.getAudioParam) sum on top of the offset, so
   * an LFO physically modulates pulse width sample-by-sample.
   */
  readonly width = new NumericParameter({
    name: 'Width',
    default: 0.5,
    min: 0.02,
    max: 0.98,
    curve: 'linear',
    smoothingMs: 15,
    randomSkew: 1,
    formatter: (v) => `${Math.round(v * 100)}%`,
  });
  /**
   * Per-voice wavefold drive. Same idea as the FOLD effect, but
   * applied inside each Voice between osGain and filter — every
   * note gets its own folder so the harmonic content evolves with
   * each voice's envelope independently. At 0 the signal passes
   * through dry (no fold artifacts); at 1 the input is pushed
   * hard into the waveshaper for full Buchla-style fold richness.
   * Voice's internal subgraph crossfades dry/wet by this value
   * AND scales preGain (1 → 6) so cranking Drive both adds wet
   * AND increases the input level pushing into the curve.
   */
  readonly drive = new NumericParameter({
    name: 'Drive',
    default: 0,
    min: 0,
    max: 1,
    curve: 'linear',
    smoothingMs: 30,
    // Bias slightly low — a random patch with full drive routinely
    // dominates the rest of the chain.
    randomSkew: 0.7,
    formatter: (v) => `${Math.round(v * 100)}%`,
  });
  /**
   * Per-voice wavefold count. Same convention as the FOLD effect:
   * the waveshaper curve is `sin(π·x·fold)`, so larger values
   * produce more zero-crossings within the [-1, +1] range and a
   * busier harmonic stack. Default 2 ≈ near-triangle approximation.
   */
  readonly fold = new NumericParameter({
    name: 'Fold',
    default: 2,
    min: 1,
    max: 8,
    curve: 'linear',
    smoothingMs: 30,
    randomSkew: 0.6,
    formatter: (v) => v.toFixed(2),
  });

  readonly parameters: readonly AnyParameter[] = [
    this.waveform,
    this.coarse,
    this.detune,
    this.level,
    this.width,
    this.drive,
    this.fold,
  ];

  /** Oscillators are sources — no audio input. */
  readonly input: AudioNode | null = null;

  private osc: OscillatorNode | null = null;
  private levelGain: GainNode | null = null;
  private readonly unsubs: Array<() => void> = [];
  private started = false;

  /**
   * Captured single-cycle wavetable, used when waveform === 'capture'.
   * 2048 samples by convention (see `wavetable.ts`). null until the
   * user hits Capture — voices treat null as silence in capture
   * mode, so the rack stays quiet rather than emitting a click.
   */
  private capturedWavetable: Float32Array | null = null;
  /** Listeners notified when the captured wavetable changes. Voices
   *  subscribe so they can rebuild their `AudioBufferSourceNode`
   *  with the new shape. */
  private readonly captureListeners = new Set<
    (data: Float32Array | null) => void
  >();

  constructor(id?: string) {
    this.id = id ?? mintId('osc');
  }

  /** Read-only access to the current wavetable. Voices use this on
   *  construction to seed their buffer; subsequent changes flow
   *  through `subscribeCapture`. */
  getCapturedWavetable(): Float32Array | null {
    return this.capturedWavetable;
  }

  /** Replace the captured wavetable. A copy is stored so the caller
   *  can reuse / mutate their own buffer. Pass null to clear. */
  setCapturedWavetable(data: Float32Array | null): void {
    this.capturedWavetable = data ? new Float32Array(data) : null;
    for (const fn of this.captureListeners) fn(this.capturedWavetable);
  }

  /** Subscribe to wavetable changes — voices wire this so they can
   *  rebuild their AudioBufferSourceNode when the user captures a
   *  new shape. Returns the usual unsubscribe function. */
  subscribeCapture(fn: (data: Float32Array | null) => void): () => void {
    this.captureListeners.add(fn);
    return () => {
      this.captureListeners.delete(fn);
    };
  }

  get output(): AudioNode | null {
    return this.levelGain;
  }

  /**
   * init() is kept for test and single-voice use. The main app runs through
   * VoiceManager which creates per-voice AudioNodes and binds those to this
   * module's parameters directly — this template never plays audio there.
   *
   * When used standalone, a default pitch of A4 (440 Hz) is applied; real use
   * would override via the osc.frequency AudioParam from the caller.
   */
  init(engine: AudioEngine): void {
    if (this.started) return;
    const ctx = engine.context;

    this.osc = ctx.createOscillator();
    this.osc.type = waveformToOscType(this.waveform.value);
    this.osc.frequency.value = 440;
    this.osc.detune.value = this.detune.value + this.coarse.value * 100;

    this.levelGain = ctx.createGain();
    this.levelGain.gain.value = this.level.value;

    this.osc.connect(this.levelGain);
    this.osc.start();
    this.started = true;

    this.detune.bindAudioParam(this.osc.detune, ctx);
    this.level.bindAudioParam(this.levelGain.gain, ctx);

    this.unsubs.push(
      this.waveform.subscribe((v) => {
        if (this.osc) this.osc.type = waveformToOscType(v);
      }),
    );
  }

  dispose(): void {
    for (const u of this.unsubs) u();
    this.unsubs.length = 0;
    if (this.osc) {
      try {
        this.osc.stop();
      } catch {
        // already stopped
      }
      this.osc.disconnect();
      this.osc = null;
    }
    this.levelGain?.disconnect();
    this.levelGain = null;
    this.started = false;
  }

  serialize(): ModuleState {
    return {
      id: this.id,
      type: this.type,
      bypass: this.bypass,
      params: writeParamState(this.parameters),
    };
  }

  deserialize(state: ModuleState): void {
    if (state.bypass !== undefined) this.bypass = state.bypass;
    readParamState(this.parameters, state.params, 'preset');
  }
}
